const SAMPLE_RATE = 24000;
const INITIAL_PREBUFFER_SECONDS = 0.18;
const REBUFFER_SECONDS = 0.12;
const START_LEAD_SECONDS = 0.04;
const UNDERRUN_GUARD_SECONDS = 0.008;
const MAX_CHUNK_BYTES = 131072;

export default class LiveVoicePlayback {
  constructor({ context, send, onIdle }) {
    this.context = context;
    this.send = send;
    this.onIdle = onIdle;
    this.sources = new Set();
    this.pending = new Map();
    this.buffers = [];
    this.bufferedSeconds = 0;
    this.responseId = null;
    this.nextSequence = 0;
    this.nextTime = 0;
    this.done = false;
    this.needsAck = false;
    this.started = false;
    this.prebufferTimer = null;
  }

  begin(responseId) {
    this.clear();
    this.responseId = responseId || null;
    this.nextSequence = 0;
    this.nextTime = 0;
    this.done = false;
    this.needsAck = false;
    this.started = false;
  }

  append(responseId, encoded, sequence) {
    if (!this.responseId || responseId !== this.responseId) return false;
    if (this.done) return false;

    const seq = Number(sequence);
    if (!Number.isInteger(seq) || seq < this.nextSequence || seq > this.nextSequence + 64) {
      throw new Error("audio_sequence");
    }

    if (!this.pending.has(seq)) this.pending.set(seq, encoded);
    this.drainPending();
    this.maybeSchedule();
    return true;
  }

  decode(encoded) {
    const raw = atob(String(encoded || ""));
    if (!raw.length || raw.length % 2 || raw.length > MAX_CHUNK_BYTES) {
      throw new Error("audio_format");
    }

    const bytes = Uint8Array.from(raw, (char) => char.charCodeAt(0));
    const pcm = new DataView(bytes.buffer);
    const buffer = this.context.createBuffer(1, bytes.length / 2, SAMPLE_RATE);
    const channel = buffer.getChannelData(0);

    for (let i = 0; i < channel.length; i += 1) {
      channel[i] = pcm.getInt16(i * 2, true) / 32768;
    }

    return buffer;
  }

  drainPending() {
    while (this.pending.has(this.nextSequence)) {
      const encoded = this.pending.get(this.nextSequence);
      this.pending.delete(this.nextSequence);
      const buffer = this.decode(encoded);
      this.buffers.push(buffer);
      this.bufferedSeconds += buffer.duration;
      this.nextSequence += 1;
    }
  }

  maybeSchedule() {
    if (!this.responseId || !this.buffers.length) return;

    const now = this.context.currentTime;
    const timelineHealthy = this.started && this.nextTime > now + UNDERRUN_GUARD_SECONDS;

    if (timelineHealthy) {
      this.scheduleBuffered(this.nextTime);
      return;
    }

    const target = this.started ? REBUFFER_SECONDS : INITIAL_PREBUFFER_SECONDS;
    if (this.bufferedSeconds >= target || this.done) {
      this.startBufferedRun();
      return;
    }

    // Network audio arrives in small PCM deltas. Give the browser a small jitter
    // buffer before starting (and after a real underrun) so transient network or
    // main-thread jitter does not create a gap between every delta.
    if (!this.prebufferTimer) {
      const waitMs = Math.max(35, Math.round(target * 1000 * 0.7));
      this.prebufferTimer = window.setTimeout(() => {
        this.prebufferTimer = null;
        if (this.responseId && this.buffers.length) this.startBufferedRun();
      }, waitMs);
    }
  }

  startBufferedRun() {
    if (!this.buffers.length) {
      this.checkDone();
      return;
    }

    window.clearTimeout(this.prebufferTimer);
    this.prebufferTimer = null;

    const now = this.context.currentTime;
    const startAt = now + START_LEAD_SECONDS;
    this.started = true;
    this.nextTime = startAt;
    this.scheduleBuffered(startAt);
  }

  scheduleBuffered(startAt) {
    let cursor = Math.max(startAt, this.nextTime || startAt);

    while (this.buffers.length) {
      const buffer = this.buffers.shift();
      this.bufferedSeconds = Math.max(0, this.bufferedSeconds - buffer.duration);

      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.context.destination);
      this.sources.add(source);

      source.onended = () => {
        try {
          source.disconnect();
        } catch {}
        this.sources.delete(source);
        this.checkDone();
      };

      source.start(cursor);
      cursor += buffer.duration;
    }

    this.nextTime = cursor;
  }

  finish(responseId, needsAck) {
    if (responseId !== this.responseId) return;
    this.done = true;
    this.needsAck = Boolean(needsAck);
    this.drainPending();

    if (this.pending.size) {
      throw new Error("audio_sequence");
    }

    this.maybeSchedule();
    this.checkDone();
  }

  checkDone() {
    if (!this.responseId || !this.done) return;
    if (this.pending.size || this.buffers.length || this.sources.size || this.prebufferTimer) return;

    const responseId = this.responseId;
    const needsAck = this.needsAck;
    this.responseId = null;
    this.nextTime = 0;
    this.started = false;

    if (needsAck) {
      this.send({ type: "playback.done", response_id: responseId });
    }

    this.onIdle?.();
  }

  clear() {
    this.responseId = null;
    this.pending.clear();
    this.buffers = [];
    this.bufferedSeconds = 0;
    this.done = false;
    this.needsAck = false;
    this.nextSequence = 0;
    this.nextTime = 0;
    this.started = false;
    window.clearTimeout(this.prebufferTimer);
    this.prebufferTimer = null;

    for (const source of this.sources) {
      source.onended = null;
      try {
        source.stop();
      } catch {}
      try {
        source.disconnect();
      } catch {}
    }

    this.sources.clear();
  }
}
